import { useEffect, useRef, useState } from "react";
import { saveRect, deleteRect, deleteLocation } from "./IcleanViewApi";
import { computeFloorplanHandle } from "./handles";

export const useLocationEdit = ({
  svgRef,
  rectsRef,
  setRects,
  editMode,
  selectedId,
  setSelectedId,
  selectedCompanyId,
  drawMode,
  setDrawMode,
  fileInputRef,
  floorplanEditMode,
  setFloorplanEditMode,
  floorplanEditLocation,
  setFloorplanEditLocation,
  editLoctagMode,
  addMode,
  onLoctagClick,
}) => {
  const draftRef = useRef(null);
  const modeRef = useRef("idle");
  const originRef = useRef({ x: 0, y: 0 });
  const drawStartScreenRef = useRef({ x: 0, y: 0 });
  const activeRectIdRef = useRef(null);
  const cropEdgeRef = useRef(null);
  const [floorplanCropMode, setFloorplanCropMode] = useState(false);
  const [cropSelection, setCropSelection] = useState(null);
  const pendingCropRef = useRef(null);
  
  const getStoredCanvasOffset = () => ({ x: 0, y: 0 });
  const getStoredCanvasZoom = () => 1.0;
  
  const [canvasOffset, setCanvasOffset] = useState(getStoredCanvasOffset());
  const [canvasZoom, setCanvasZoom] = useState(getStoredCanvasZoom());
  const draggingCanvas = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });
  const resizeStartRef = useRef(null);
  const lastTouchDistance = useRef(null);
  const lastTouchCenter = useRef(null);
  const animationRef = useRef(null);

  // Animáltan rázoomol/pánoz egy adott world-bounds-ra (x, y, width, height).
  // Ha bounds === null → nem csinál semmit.
  // options.rightInset: jobb oldali takart sáv (pl. nyitott jobb panel) szélessége px-ben.
  // options.topInset / leftInset / bottomInset: hasonlóan a többi oldalra.
  const animateToView = (bounds, options = {}) => {
    if (!svgRef?.current || !bounds) return;
    const {
      duration = 450,
      padding = 0.9,
      rightInset = 0,
      leftInset = 0,
      topInset = 0,
      bottomInset = 0,
      topAlign = false,
    } = options;
    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    const vpW = rect.width;
    const vpH = rect.height;
    if (vpW <= 0 || vpH <= 0) return;
    const { x, y, width: w, height: h } = bounds;
    if (!(w > 0) || !(h > 0)) return;

    // Látható (nem takart) terület mérete és középpontja.
    const visW = Math.max(50, vpW - leftInset - rightInset);
    const visH = Math.max(50, vpH - topInset - bottomInset);
    const visCenterX = leftInset + visW / 2;
    const visCenterY = topInset + visH / 2;

    const targetZoom = Math.min(visW / w, visH / h) * padding;
    const targetOffsetX = visCenterX - (x + w / 2) * targetZoom;
    // topAlign: felső margó = oldalsó margóval egyenlő (szimmetrikus elrendezés)
    // normál: vertikálisan középre igazít
    const sideMargin = Math.min(
      (visW - w * targetZoom) / 2,
      (visH - h * targetZoom) / 2
    );
    const targetOffsetY = topAlign
      ? topInset + sideMargin - y * targetZoom
      : visCenterY - (y + h / 2) * targetZoom;

    const startOffset = canvasOffset;
    const startZoom = canvasZoom;
    const startTime = performance.now();
    if (animationRef.current) cancelAnimationFrame(animationRef.current);

    const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

    const tick = (now) => {
      const t = Math.min(1, (now - startTime) / duration);
      const e = easeInOut(t);
      setCanvasZoom(startZoom + (targetZoom - startZoom) * e);
      setCanvasOffset({
        x: startOffset.x + (targetOffsetX - startOffset.x) * e,
        y: startOffset.y + (targetOffsetY - startOffset.y) * e,
      });
      if (t < 1) {
        animationRef.current = requestAnimationFrame(tick);
      } else {
        animationRef.current = null;
      }
    };
    animationRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);
  
  // Tap detection for mobile loctag placement
  const tapStartTime = useRef(null);
  const tapStartPos = useRef(null);

  const calculateDepths = () => {
    const map = new Map();
    rectsRef.current.forEach((r) => map.set(r.id, r));
    const getDepth = (r) => {
      let depth = 0;
      let current = r;
      while (current.parent_location_id && map.has(current.parent_location_id)) {
        current = map.get(current.parent_location_id);
        depth++;
      }
      return depth;
    };
    rectsRef.current.forEach((r) => {
      r._depth = getDepth(r);
    });
  };

  const updateRender = () => {
    calculateDepths();
    const sorted = [...rectsRef.current].sort((a, b) => a._depth - b._depth);
    setRects(sorted);
  };

  const getChildRects = (parentId) => {
    const collect = [];
    const stack = [parentId];
    while (stack.length) {
      const current = stack.pop();
      rectsRef.current.forEach((r) => {
        if (r.parent_location_id === current) {
          collect.push(r);
          stack.push(r.id);
        }
      });
    }
    return collect;
  };

  const handleMouseDown = (e) => {
    if (!svgRef.current) return;
    if (!editMode) {
      if (e.selectOnly && e.hoveredId) {
        setSelectedId(e.hoveredId);
        return;
      }
      draggingCanvas.current = true;
      lastPos.current = { x: e.clientX, y: e.clientY };
      return;
    }
    if (e.target.closest("input") || e.target.closest("select")) return;

    // Floorplan edit mode: kép drag-olása vagy crop/resize
    if (floorplanEditMode && floorplanEditLocation) {
      const pt = svgRef.current.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
      const x = (svgP.x - canvasOffset.x) / canvasZoom;
      const y = (svgP.y - canvasOffset.y) / canvasZoom;
      // Crop mód: téglalap kijelölés indítása
      if (floorplanCropMode) {
        // csak a kép területén belül
        const imgX = floorplanEditLocation.x + (floorplanEditLocation.floorplan_offset_x || 0);
        const imgY = floorplanEditLocation.y + (floorplanEditLocation.floorplan_offset_y || 0);
        const imgW = floorplanEditLocation.floorplan_width || floorplanEditLocation.width;
        const imgH = floorplanEditLocation.floorplan_height || floorplanEditLocation.height;
        if (x >= imgX && x <= imgX + imgW && y >= imgY && y <= imgY + imgH) {
          modeRef.current = "floorplan_rect_select";
          originRef.current = { x, y };
          const sel = { x, y, width: 0, height: 0 };
          setCropSelection(sel);
          pendingCropRef.current = sel;
          return;
        }
      }

      const handle = computeFloorplanHandle(floorplanEditLocation, x, y, 6);
      if (handle === "inside") {
        modeRef.current = "pending_floorplan_drag";
        originRef.current = { x, y };
        return;
      }
      if (handle && handle !== "inside") {
        if (floorplanCropMode) {
          modeRef.current = "floorplan_cropping";
        } else {
          modeRef.current = "floorplan_resizing";
        }
        activeRectIdRef.current = floorplanEditLocation.id;
        originRef.current = { x, y };
        cropEdgeRef.current = handle; // edge or corner
        // Capture start box for stable resizing
        const r = rectsRef.current.find(rr => rr.id === floorplanEditLocation.id) || floorplanEditLocation;
        const startOffX = r.floorplan_offset_x || 0;
        const startOffY = r.floorplan_offset_y || 0;
        const startW = r.floorplan_width || r.width;
        const startH = r.floorplan_height || r.height;
        resizeStartRef.current = {
          startX: x,
          startY: y,
          left: startOffX,
          right: startOffX + startW,
          top: startOffY,
          bottom: startOffY + startH,
          width: startW,
          height: startH,
          aspect: startW / Math.max(1, startH),
          handle,
        };
        return;
      }
      // Kívül kattintás: kilépés
      setFloorplanEditMode(false);
      setFloorplanEditLocation(null);
      return;
    }

    // Normál edit mode logika
    if (!editMode) {
      return;
    }

    // Draw mode: új location rajzolása
    if (drawMode) {
      draggingCanvas.current = false;
      const pt = svgRef.current.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
      const x = (svgP.x - canvasOffset.x) / canvasZoom;
      const y = (svgP.y - canvasOffset.y) / canvasZoom;
      
      setSelectedId(null);
      modeRef.current = "drawing";
      draftRef.current = { x, y, width: 0, height: 0 };
      originRef.current = { x, y };
      drawStartScreenRef.current = { x: svgP.x, y: svgP.y };
      return;
    }

    // Új logika: ha van hoveredId, azt válassza ki, ha nincs, törölje a kiválasztást
    if (e.hoveredId) {
      draggingCanvas.current = false;
      const targetId = e.hoveredId;
      setSelectedId(targetId);
      // Drag/resize azonnal indulhat az aktuálisan kattintott elemen
      if (e.hoveredEdge) {
        const pt = svgRef.current.createSVGPoint();
        pt.x = e.clientX;
        pt.y = e.clientY;
        const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
        const x = (svgP.x - canvasOffset.x) / canvasZoom;
        const y = (svgP.y - canvasOffset.y) / canvasZoom;
        if (e.hoveredEdge === "move") {
          modeRef.current = "dragging";
          activeRectIdRef.current = targetId;
          originRef.current = { x, y };
          return;
        } else if (["ew-resize", "ns-resize", "nwse-resize", "nesw-resize"].includes(e.hoveredEdge)) {
          const rect = rectsRef.current.find((r) => r.id === targetId);
          if (rect) {
            rect._resizeDir = e.hoveredEdge;
            modeRef.current = "resizing";
            activeRectIdRef.current = targetId;
            originRef.current = { x, y };
            return;
          }
        }
      }
      modeRef.current = "idle";
      return;
    }
    if (e.hoveredId === null) {
      setSelectedId(null);
      draggingCanvas.current = true;
      lastPos.current = { x: e.clientX, y: e.clientY };
      return;
    }

    const pt = svgRef.current.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
    const x = (svgP.x - canvasOffset.x) / canvasZoom;
    const y = (svgP.y - canvasOffset.y) / canvasZoom;

    const margin = 6;
    const hovered = rectsRef.current
      .filter((r) => x >= r.x && x <= r.x + r.width && y >= r.y && y <= r.y + r.height)
      .sort((a, b) => b._depth - a._depth);

    const maxDepth = Math.max(...hovered.map(r => r._depth), -1);
    const selected = hovered.find(r => r._depth === maxDepth);

    const isInner = selected
      ? x > selected.x + margin &&
        x < selected.x + selected.width - margin &&
        y > selected.y + margin &&
        y < selected.y + selected.height - margin
      : false;

    if (selectedId && selectedId === selected?.id) {
      const edge = {
        top: Math.abs(y - selected.y) <= margin,
        bottom: Math.abs(y - (selected.y + selected.height)) <= margin,
        left: Math.abs(x - selected.x) <= margin,
        right: Math.abs(x - (selected.x + selected.width)) <= margin,
      };

      const isCorner = (edge.top || edge.bottom) && (edge.left || edge.right);
      const isSide = (edge.top || edge.bottom || edge.left || edge.right) && !isCorner;

      if (isCorner) {
        const resizeDir = [];
        if (edge.left) resizeDir.push("w");
        if (edge.right) resizeDir.push("e");
        if (edge.top) resizeDir.push("n");
        if (edge.bottom) resizeDir.push("s");
        const rect = rectsRef.current.find((r) => r.id === selectedId);
        rect._resizeDir = resizeDir.join("");
        modeRef.current = "resizing";
        activeRectIdRef.current = selectedId;
        originRef.current = { x, y };
        return;
      }

      if (isSide) {
        modeRef.current = "dragging";
        activeRectIdRef.current = selectedId;
        originRef.current = { x, y };
        return;
      }

      // Törlés csak dedikált delete ikonról történhet.
      // Itt csak maradjon kijelölve az elem.
      setSelectedId(selectedId);
      modeRef.current = "idle";
      return;
    }

    if (!hovered.length || (selected && isInner)) {
      setSelectedId(null);
      modeRef.current = "drawing";
      draftRef.current = { x, y, width: 0, height: 0 };
      originRef.current = { x, y };
      return;
    }

    setSelectedId(selected.id);
    modeRef.current = "idle";
  };

  const handleCanvasDrag = (e) => {
    if (!draggingCanvas.current) return;
    if (e.buttons !== 1) {
      draggingCanvas.current = false;
      return;
    }
    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;
    lastPos.current = { x: e.clientX, y: e.clientY };
    setCanvasOffset((prev) => ({
      x: (prev?.x || 0) + dx,
      y: (prev?.y || 0) + dy,
    }));
  };

  const handleCanvasDragEnd = () => {
    draggingCanvas.current = false;
  };

  const handleCanvasZoom = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!svgRef.current) return;

    const pt = svgRef.current.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());

    const sensitivity = 0.001;
    const zoomDelta = -e.deltaY * sensitivity;

    setCanvasZoom((prevZoom) => {
      const newZoom = Math.min(Math.max(prevZoom + zoomDelta, 0.1), 5.0);

      setCanvasOffset((prevOffset) => {
        const currentOffset = prevOffset || { x: 0, y: 0 };
        const mouseX = (svgP.x - currentOffset.x) / prevZoom;
        const mouseY = (svgP.y - currentOffset.y) / prevZoom;

        return {
          x: svgP.x - mouseX * newZoom,
          y: svgP.y - mouseY * newZoom,
        };
      });

      return newZoom;
    });
  };

  // Touch event handlers - mobil támogatás
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const handleTouchStart = (e) => {
      if (e.target.closest('foreignObject') || e.target.closest('button')) {
        return;
      }
      if (editLoctagMode && !addMode && (e.target.tagName === 'circle' || e.target.classList.contains('loctag-circle'))) {
        return;
      }

      if (e.touches.length === 1) {
        const touch = e.touches[0];
        if (editLoctagMode && addMode) {
          tapStartTime.current = Date.now();
          tapStartPos.current = { x: touch.clientX, y: touch.clientY };
          draggingCanvas.current = false;
        } else {
          tapStartTime.current = null;
          tapStartPos.current = null;
          draggingCanvas.current = true;
          lastPos.current = { x: touch.clientX, y: touch.clientY };
        }
        lastTouchDistance.current = null;
        lastTouchCenter.current = null;
      } else if (e.touches.length === 2) {
        tapStartTime.current = null;
        tapStartPos.current = null;
        draggingCanvas.current = false;
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        lastTouchDistance.current = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );
        lastTouchCenter.current = {
          x: (touch1.clientX + touch2.clientX) / 2,
          y: (touch1.clientY + touch2.clientY) / 2,
        };
      }
    };

    const handleTouchMove = (e) => {
      if (editLoctagMode && !addMode && (e.target.tagName === 'circle' || e.target.classList.contains('loctag-circle'))) {
        return;
      }
      if (editLoctagMode && addMode && tapStartTime.current && tapStartPos.current && e.touches.length === 1) {
        const touch = e.touches[0];
        const movement = Math.hypot(
          touch.clientX - tapStartPos.current.x,
          touch.clientY - tapStartPos.current.y
        );
        if (movement > 10) {
          tapStartTime.current = null;
          tapStartPos.current = null;
          draggingCanvas.current = true;
          lastPos.current = { x: touch.clientX, y: touch.clientY };
        }
      }

      if (draggingCanvas.current && e.touches.length === 1) {
        const touch = e.touches[0];
        const dx = touch.clientX - lastPos.current.x;
        const dy = touch.clientY - lastPos.current.y;
        lastPos.current = { x: touch.clientX, y: touch.clientY };
        setCanvasOffset((prev) => ({
          x: (prev?.x || 0) + dx,
          y: (prev?.y || 0) + dy,
        }));
        return;
      }

      if (e.touches.length === 2) {
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const distance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );

        if (lastTouchDistance.current) {
          const scale = distance / lastTouchDistance.current;
          setCanvasZoom((prevZoom) => {
            const newZoom = Math.min(Math.max(prevZoom * scale, 0.1), 5.0);

            if (svg && lastTouchCenter.current) {
              const pt = svg.createSVGPoint();
              pt.x = lastTouchCenter.current.x;
              pt.y = lastTouchCenter.current.y;
              const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());

              setCanvasOffset((prevOffset) => {
                const currentOffset = prevOffset || { x: 0, y: 0 };
                const mouseX = (svgP.x - currentOffset.x) / prevZoom;
                const mouseY = (svgP.y - currentOffset.y) / prevZoom;

                return {
                  x: svgP.x - mouseX * newZoom,
                  y: svgP.y - mouseY * newZoom,
                };
              });
            }

            return newZoom;
          });
        }

        lastTouchDistance.current = distance;
        lastTouchCenter.current = {
          x: (touch1.clientX + touch2.clientX) / 2,
          y: (touch1.clientY + touch2.clientY) / 2,
        };
      }
    };

    const handleTouchEnd = (e) => {
      if (editLoctagMode && !addMode && (e.target.tagName === 'circle' || e.target.classList.contains('loctag-circle'))) {
        return;
      }

      if (editLoctagMode && addMode && tapStartTime.current) {
        const duration = Date.now() - tapStartTime.current;
        const touch = e.changedTouches[0];
        const movement = tapStartPos.current ? Math.hypot(
          touch.clientX - tapStartPos.current.x,
          touch.clientY - tapStartPos.current.y
        ) : 0;

        if (duration < 200 && movement < 10) {
          const syntheticEvent = {
            clientX: touch.clientX,
            clientY: touch.clientY,
            preventDefault: () => {},
            stopPropagation: () => {}
          };
          if (onLoctagClick) {
            onLoctagClick(syntheticEvent);
          }
        }
      }

      tapStartTime.current = null;
      tapStartPos.current = null;

      if (e.touches.length === 0) {
        draggingCanvas.current = false;
        lastTouchDistance.current = null;
        lastTouchCenter.current = null;
      }
    };

    svg.addEventListener('touchstart', handleTouchStart, { passive: false });
    svg.addEventListener('touchmove', handleTouchMove, { passive: false });
    svg.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      svg.removeEventListener('touchstart', handleTouchStart);
      svg.removeEventListener('touchmove', handleTouchMove);
      svg.removeEventListener('touchend', handleTouchEnd);
    };
  }, [editLoctagMode, addMode, onLoctagClick, svgRef]);

  // Wheel event listener az SVG-hez - TELJESEN megakadályozza a böngésző zoom-ját
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const handleWheel = (e) => {
      // MINDIG megakadályozzuk a böngésző zoom-ját
      e.preventDefault();
      e.stopPropagation();
      handleCanvasZoom(e);
    };

    // passive: false KÖTELEZŐ, különben preventDefault nem működik!
    svg.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      svg.removeEventListener('wheel', handleWheel);
    };
  }, [svgRef]);

  useEffect(() => {
    let draggingOrResizing = false;
    const handleMouseMove = (e) => {
      if (!svgRef.current) return;

      // Floorplan-specific modes work independently of editMode
      const isFloorplanMode = ["pending_floorplan_drag","floorplan_dragging","floorplan_resizing","floorplan_cropping","floorplan_rect_select"].includes(modeRef.current);
      if (!isFloorplanMode && !editMode) return;

      // If mouse button released mid-drag, reset floorplan drag state
      if (isFloorplanMode && e.buttons === 0) {
        modeRef.current = "idle";
        return;
      }

      if ((modeRef.current === "dragging" || modeRef.current === "resizing") && e.buttons === 0) {
        modeRef.current = "idle";
        activeRectIdRef.current = null;
        draggingOrResizing = false;
        return;
      }

      const pt = svgRef.current.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
      const x = (svgP.x - canvasOffset.x) / canvasZoom;
      const y = (svgP.y - canvasOffset.y) / canvasZoom;

      if (draggingCanvas.current && modeRef.current === "idle") {
        handleCanvasDrag(e);
        return;
      }

      if (modeRef.current === "drawing" && draftRef.current) {
        const deltaX = (svgP.x - drawStartScreenRef.current.x) / canvasZoom;
        const deltaY = (svgP.y - drawStartScreenRef.current.y) / canvasZoom;
        draftRef.current.width = deltaX;
        draftRef.current.height = deltaY;
        updateRender();
        return;
      } else if (["dragging", "resizing"].includes(modeRef.current) && activeRectIdRef.current) {
        draggingOrResizing = true;
        const dx = x - originRef.current.x;
        const dy = y - originRef.current.y;
        const rect = rectsRef.current.find((r) => r.id === activeRectIdRef.current);
        if (rect) {
          if (modeRef.current === "dragging") {
            rect.x += dx;
            rect.y += dy;
            const children = getChildRects(rect.id);
            for (const child of children) {
              child.x += dx;
              child.y += dy;
            }
            saveRect(rect, selectedCompanyId);
            for (const child of children) saveRect(child, selectedCompanyId);
          } else if (modeRef.current === "resizing") {
            const dir = rect._resizeDir;
            
            // Régi méretek mentése floorplan skálázáshoz
            const oldWidth = rect.width;
            const oldHeight = rect.height;
            const prevRect = {
              x: rect.x,
              y: rect.y,
              width: rect.width,
              height: rect.height,
            };
            
            // Edge resize
            if (dir === "ew-resize") {
              // Left or right edge
              if (Math.abs(originRef.current.x - rect.x) < Math.abs(originRef.current.x - (rect.x + rect.width))) {
                // Left edge
                rect.width -= dx;
                rect.x += dx;
              } else {
                // Right edge
                rect.width += dx;
              }
            } else if (dir === "ns-resize") {
              // Top or bottom edge
              if (Math.abs(originRef.current.y - rect.y) < Math.abs(originRef.current.y - (rect.y + rect.height))) {
                // Top edge
                rect.height -= dy;
                rect.y += dy;
              } else {
                // Bottom edge
                rect.height += dy;
              }
            } else if (dir === "nwse-resize") {
              // Top-left or bottom-right corner
              if (Math.abs(originRef.current.x - rect.x) < Math.abs(originRef.current.x - (rect.x + rect.width))) {
                // Left
                rect.width -= dx;
                rect.x += dx;
              } else {
                // Right
                rect.width += dx;
              }
              if (Math.abs(originRef.current.y - rect.y) < Math.abs(originRef.current.y - (rect.y + rect.height))) {
                // Top
                rect.height -= dy;
                rect.y += dy;
              } else {
                // Bottom
                rect.height += dy;
              }
            } else if (dir === "nesw-resize") {
              // Top-right or bottom-left corner
              if (Math.abs(originRef.current.x - rect.x) > Math.abs(originRef.current.x - (rect.x + rect.width))) {
                // Right
                rect.width += dx;
              } else {
                // Left
                rect.width -= dx;
                rect.x += dx;
              }
              if (Math.abs(originRef.current.y - rect.y) < Math.abs(originRef.current.y - (rect.y + rect.height))) {
                // Top
                rect.height -= dy;
                rect.y += dy;
              } else {
                // Bottom
                rect.height += dy;
              }
            }
            
            // Floorplan arányos skálázás
            if (rect.floorplan_id && oldWidth > 0 && oldHeight > 0) {
              const scaleX = rect.width / oldWidth;
              const scaleY = rect.height / oldHeight;
              
              if (rect.floorplan_width) rect.floorplan_width *= scaleX;
              if (rect.floorplan_height) rect.floorplan_height *= scaleY;
              if (rect.floorplan_offset_x) rect.floorplan_offset_x *= scaleX;
              if (rect.floorplan_offset_y) rect.floorplan_offset_y *= scaleY;
              
              if (typeof rect.floorplan_crop_left === 'number') rect.floorplan_crop_left *= scaleX;
              if (typeof rect.floorplan_crop_top === 'number') rect.floorplan_crop_top *= scaleY;
              if (typeof rect.floorplan_crop_right === 'number') rect.floorplan_crop_right *= scaleX;
              if (typeof rect.floorplan_crop_bottom === 'number') rect.floorplan_crop_bottom *= scaleY;
            }
            
            let scaleX = 1;
            let scaleY = 1;
            if (oldWidth > 0) scaleX = rect.width / oldWidth;
            if (oldHeight > 0) scaleY = rect.height / oldHeight;

            const children = getChildRects(rect.id);
            if (children.length && (scaleX !== 1 || scaleY !== 1)) {
              const shiftX = rect.x - prevRect.x;
              const shiftY = rect.y - prevRect.y;
              for (const child of children) {
                const relX = child.x - prevRect.x;
                const relY = child.y - prevRect.y;

                child.x = rect.x + relX * scaleX;
                child.y = rect.y + relY * scaleY;

                if (typeof child.width === 'number') child.width *= scaleX;
                if (typeof child.height === 'number') child.height *= scaleY;

                // Preserve visual offset relative to the resized parent
                if (typeof child.floorplan_offset_x === 'number') {
                  child.floorplan_offset_x = (child.floorplan_offset_x + shiftX) * scaleX;
                }
                if (typeof child.floorplan_offset_y === 'number') {
                  child.floorplan_offset_y = (child.floorplan_offset_y + shiftY) * scaleY;
                }

                if (child.floorplan_id) {
                  if (typeof child.floorplan_width === 'number') child.floorplan_width *= scaleX;
                  if (typeof child.floorplan_height === 'number') child.floorplan_height *= scaleY;
                  if (typeof child.floorplan_crop_left === 'number') child.floorplan_crop_left *= scaleX;
                  if (typeof child.floorplan_crop_top === 'number') child.floorplan_crop_top *= scaleY;
                  if (typeof child.floorplan_crop_right === 'number') child.floorplan_crop_right *= scaleX;
                  if (typeof child.floorplan_crop_bottom === 'number') child.floorplan_crop_bottom *= scaleY;
                }
              }
            }

            saveRect(rect, selectedCompanyId);
            for (const child of children) {
              saveRect(child, selectedCompanyId);
            }
          }
          originRef.current = { x, y };
          updateRender();
        }
      }
      if (modeRef.current === "pending_floorplan_drag" && floorplanEditLocation) {
        const pt2 = svgRef.current.createSVGPoint();
        pt2.x = e.clientX; pt2.y = e.clientY;
        const svgP2 = pt2.matrixTransform(svgRef.current.getScreenCTM().inverse());
        const x2 = (svgP2.x - canvasOffset.x) / canvasZoom;
        const y2 = (svgP2.y - canvasOffset.y) / canvasZoom;
        if (Math.hypot(x2 - originRef.current.x, y2 - originRef.current.y) > 3 / canvasZoom) {
          modeRef.current = "floorplan_dragging";
        }
        return;
      }
      if (modeRef.current === "floorplan_dragging" && floorplanEditLocation) {
        const pt = svgRef.current.createSVGPoint();
        pt.x = e.clientX;
        pt.y = e.clientY;
        const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
        const x = (svgP.x - canvasOffset.x) / canvasZoom;
        const y = (svgP.y - canvasOffset.y) / canvasZoom;
        
        const deltaX = x - originRef.current.x;
        const deltaY = y - originRef.current.y;
        
        // Frissítjük a floorplan offset értékeket
        const newOffsetX = (floorplanEditLocation.floorplan_offset_x || 0) + deltaX;
        const newOffsetY = (floorplanEditLocation.floorplan_offset_y || 0) + deltaY;
        
        // Frissítjük a state-et
        const updatedLocation = {
          ...floorplanEditLocation,
          floorplan_offset_x: newOffsetX,
          floorplan_offset_y: newOffsetY
        };
        setFloorplanEditLocation(updatedLocation);
        
        // Frissítjük a rectsRef.current-ben is
        const rectIndex = rectsRef.current.findIndex(r => r.id === floorplanEditLocation.id);
        if (rectIndex !== -1) {
          rectsRef.current[rectIndex] = {
            ...rectsRef.current[rectIndex],
            floorplan_offset_x: newOffsetX,
            floorplan_offset_y: newOffsetY
          };
        }
        
        // Azonnali render az elmozdításra
        updateRender();

        // Frissítjük az origin-t a következő frame-hez
        originRef.current = { x, y };
        return;
      }
      
      if (modeRef.current === "floorplan_resizing" && floorplanEditLocation) {
        const pt = svgRef.current.createSVGPoint();
        pt.x = e.clientX;
        pt.y = e.clientY;
        const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
        const x = (svgP.x - canvasOffset.x) / canvasZoom;
        const y = (svgP.y - canvasOffset.y) / canvasZoom;

        const start = resizeStartRef.current;
        if (!start) return;

        const dx = x - start.startX;
        const dy = y - start.startY;

        const rectIndex = rectsRef.current.findIndex(r => r.id === floorplanEditLocation.id);
        if (rectIndex !== -1) {
          const rect = rectsRef.current[rectIndex];
          const handle = start.handle;
          const minSize = 10;

          // Work on sides based on start box
          let left = start.left;
          let right = start.right;
          let top = start.top;
          let bottom = start.bottom;

          const isCorner = ["top-left","top-right","bottom-left","bottom-right"].includes(handle);

          if (!isCorner) {
            // Edge resize: only grabbed side moves; opposite and perpendicular sides stay
            // Start box values
            const sLeft = start.left;
            const sRight = start.right;
            const sTop = start.top;
            const sBottom = start.bottom;

            if (handle === "left") {
              const left = sLeft + dx;
              const right = sRight;
              const w = Math.max(minSize, right - left);
              rect.floorplan_offset_x = right - w; // ensure opposite (right) fixed
              rect.floorplan_width = w;
              rect.floorplan_offset_y = sTop;
              rect.floorplan_height = sBottom - sTop;
            } else if (handle === "right") {
              const right = sRight + dx;
              const left = sLeft;
              const w = Math.max(minSize, right - left);
              rect.floorplan_offset_x = left; // left fixed
              rect.floorplan_width = w;
              rect.floorplan_offset_y = sTop;
              rect.floorplan_height = sBottom - sTop;
            } else if (handle === "top") {
              const top = sTop + dy;
              const bottom = sBottom;
              const h = Math.max(minSize, bottom - top);
              rect.floorplan_offset_y = bottom - h; // bottom fixed
              rect.floorplan_height = h;
              rect.floorplan_offset_x = sLeft;
              rect.floorplan_width = sRight - sLeft;
            } else if (handle === "bottom") {
              const bottom = sBottom + dy;
              const top = sTop;
              const h = Math.max(minSize, bottom - top);
              rect.floorplan_offset_y = top; // top fixed
              rect.floorplan_height = h;
              rect.floorplan_offset_x = sLeft;
              rect.floorplan_width = sRight - sLeft;
            }
          } else {
            // Corner with aspect ratio preserved, opposite corner fixed
            const aspect = start.aspect;
            if (handle === "top-left") {
              // Anchor: bottom-right = (start.right, start.bottom)
              const candLeft = start.left + dx;
              const candTop = start.top + dy;
              // Compute width/height from anchor keeping aspect
              let newW = start.right - candLeft;
              let newH = start.bottom - candTop;
              // Choose scale so aspect holds by max constraint
              const s = Math.max(newW / start.width, newH / start.height);
              newW = Math.max(minSize, Math.round(start.width * s));
              newH = Math.max(minSize, Math.round(newW / aspect));
              left = start.right - newW;
              top = start.bottom - newH;
              rect.floorplan_offset_x = left;
              rect.floorplan_offset_y = top;
              rect.floorplan_width = newW;
              rect.floorplan_height = newH;
            } else if (handle === "top-right") {
              // Anchor: bottom-left = (start.left, start.bottom)
              const candRight = start.right + dx;
              const candTop = start.top + dy;
              let newW = candRight - start.left;
              let newH = start.bottom - candTop;
              const s = Math.max(newW / start.width, newH / start.height);
              newW = Math.max(minSize, Math.round(start.width * s));
              newH = Math.max(minSize, Math.round(newW / aspect));
              top = start.bottom - newH;
              rect.floorplan_offset_x = start.left;
              rect.floorplan_offset_y = top;
              rect.floorplan_width = newW;
              rect.floorplan_height = newH;
            } else if (handle === "bottom-left") {
              // Anchor: top-right = (start.right, start.top)
              const candLeft = start.left + dx;
              const candBottom = start.bottom + dy;
              let newW = start.right - candLeft;
              let newH = candBottom - start.top;
              const s = Math.max(newW / start.width, newH / start.height);
              newW = Math.max(minSize, Math.round(start.width * s));
              newH = Math.max(minSize, Math.round(newW / aspect));
              left = start.right - newW;
              rect.floorplan_offset_x = left;
              rect.floorplan_offset_y = start.top;
              rect.floorplan_width = newW;
              rect.floorplan_height = newH;
            } else if (handle === "bottom-right") {
              // Anchor: top-left = (start.left, start.top)
              const candRight = start.right + dx;
              const candBottom = start.bottom + dy;
              let newW = candRight - start.left;
              let newH = candBottom - start.top;
              const s = Math.max(newW / start.width, newH / start.height);
              newW = Math.max(minSize, Math.round(start.width * s));
              newH = Math.max(minSize, Math.round(newW / aspect));
              rect.floorplan_offset_x = start.left;
              rect.floorplan_offset_y = start.top;
              rect.floorplan_width = newW;
              rect.floorplan_height = newH;
            }
          }

          setFloorplanEditLocation({
            ...floorplanEditLocation,
            floorplan_width: rect.floorplan_width,
            floorplan_height: rect.floorplan_height,
            floorplan_offset_x: rect.floorplan_offset_x,
            floorplan_offset_y: rect.floorplan_offset_y,
          });

          updateRender();
        }
        return;
      }

      if (modeRef.current === "floorplan_cropping" && floorplanEditLocation) {
        const pt = svgRef.current.createSVGPoint();
        pt.x = e.clientX;
        pt.y = e.clientY;
        const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
        const x = (svgP.x - canvasOffset.x) / canvasZoom;
        const y = (svgP.y - canvasOffset.y) / canvasZoom;
        
        const deltaX = x - originRef.current.x;
        const deltaY = y - originRef.current.y;
        
        // Frissítjük a floorplan méreteket és offset-eket a crop edge alapján
        const rectIndex = rectsRef.current.findIndex(r => r.id === floorplanEditLocation.id);
        if (rectIndex !== -1) {
          const rect = rectsRef.current[rectIndex];
          const cropEdge = cropEdgeRef.current;
          
          if (cropEdge === "left") {
            // Bal oldali crop - csak a megjelenítési terület változik
            const newCropLeft = (rect.floorplan_crop_left || 0) + deltaX;
            if (newCropLeft >= 0 && newCropLeft < (rect.floorplan_crop_right || rect.width)) {
              rect.floorplan_crop_left = newCropLeft;
            }
          } else if (cropEdge === "right") {
            // Jobb oldali crop - csak a megjelenítési terület változik
            const newCropRight = (rect.floorplan_crop_right || rect.width) + deltaX;
            if (newCropRight > (rect.floorplan_crop_left || 0) && newCropRight <= rect.width) {
              rect.floorplan_crop_right = newCropRight;
            }
          } else if (cropEdge === "top") {
            // Felső crop - csak a megjelenítési terület változik
            const newCropTop = (rect.floorplan_crop_top || 0) + deltaY;
            if (newCropTop >= 0 && newCropTop < (rect.floorplan_crop_bottom || rect.height)) {
              rect.floorplan_crop_top = newCropTop;
            }
          } else if (cropEdge === "bottom") {
            // Alsó crop - csak a megjelenítési terület változik
            const newCropBottom = (rect.floorplan_crop_bottom || rect.height) + deltaY;
            if (newCropBottom > (rect.floorplan_crop_top || 0) && newCropBottom <= rect.height) {
              rect.floorplan_crop_bottom = newCropBottom;
            }
          }
          
          // Frissítjük a state-et is
          setFloorplanEditLocation({
            ...floorplanEditLocation,
            floorplan_crop_left: rect.floorplan_crop_left,
            floorplan_crop_right: rect.floorplan_crop_right,
            floorplan_crop_top: rect.floorplan_crop_top,
            floorplan_crop_bottom: rect.floorplan_crop_bottom
          });
          
          // Frissítjük a render-t is
          updateRender();
        }
        
        // Frissítjük az origin-t a következő frame-hez
        originRef.current = { x, y };
        return;
      }

      // Téglalap alapú crop kijelölés közben
      if (modeRef.current === "floorplan_rect_select" && floorplanEditLocation && pendingCropRef.current) {
        const start = originRef.current;
        const left = Math.min(start.x, x);
        const top = Math.min(start.y, y);
        const width = Math.max(1, Math.abs(x - start.x));
        const height = Math.max(1, Math.abs(y - start.y));
        const sel = { x: left, y: top, width, height };
        pendingCropRef.current = sel;
        setCropSelection(sel);
        return;
      }
      
    };

    const handleMouseUp = () => {
      // Biztonsági reset: bármilyen ágban futunk, a canvas drag állapot ne ragadjon bent.
      handleCanvasDragEnd();

      if (modeRef.current === "pending_floorplan_drag") {
        // Click without drag — just reset, no save needed
        modeRef.current = "idle";
        return;
      }

      if (modeRef.current === "floorplan_dragging" && floorplanEditLocation) {
        // Floorplan drag befejezése - mentjük a változásokat
        saveRect(floorplanEditLocation, selectedCompanyId);
        
        // Frissítjük a rectsRef.current-ben is a végső értékeket
        const rectIndex = rectsRef.current.findIndex(r => r.id === floorplanEditLocation.id);
        if (rectIndex !== -1) {
          rectsRef.current[rectIndex] = {
            ...rectsRef.current[rectIndex],
            floorplan_offset_x: floorplanEditLocation.floorplan_offset_x,
            floorplan_offset_y: floorplanEditLocation.floorplan_offset_y
          };
          
          // Frissítjük a floorplanEditLocation state-et is a rectsRef.current-ből
          setFloorplanEditLocation(rectsRef.current[rectIndex]);
        }
        
        // Frissítjük a render-t
        updateRender();
        
        modeRef.current = "idle";
        return;
      }

      if (modeRef.current === "floorplan_resizing" && floorplanEditLocation) {
        const rectIndex = rectsRef.current.findIndex(r => r.id === floorplanEditLocation.id);
        if (rectIndex !== -1) {
          const rect = rectsRef.current[rectIndex];
          saveRect(rect, selectedCompanyId);
          setFloorplanEditLocation(rect);
          updateRender();
        }
        modeRef.current = "idle";
        activeRectIdRef.current = null;
        cropEdgeRef.current = null;
        return;
      }
      
      if (modeRef.current === "floorplan_cropping" && floorplanEditLocation) {
        // Floorplan crop befejezése - mentjük a változásokat
        const rectIndex = rectsRef.current.findIndex(r => r.id === floorplanEditLocation.id);
        if (rectIndex !== -1) {
          const rect = rectsRef.current[rectIndex];
          saveRect(rect, selectedCompanyId);
          
          // Frissítjük a floorplanEditLocation state-et is a rectsRef.current-ből
          setFloorplanEditLocation(rect);
        }
        
        // Frissítjük a render-t
        updateRender();
        
        modeRef.current = "idle";
        activeRectIdRef.current = null;
        cropEdgeRef.current = null;
        return;
      }
      
      if (modeRef.current === "floorplan_rect_select" && floorplanEditLocation && pendingCropRef.current) {
        // Kijelölés kész – maradjon meg a selection és jelenjen meg a megerősítés gomb a canvas oldalon
        modeRef.current = "idle";
        return;
      }

      if (modeRef.current === "drawing" && draftRef.current) {
        const d = draftRef.current;
        const width = Math.abs(d.width);
        const height = Math.abs(d.height);
        if (width < 10 || height < 10) {
          draftRef.current = null;
          modeRef.current = "idle";
          return;
        }
        const x = d.width < 0 ? d.x + d.width : d.x;
        const y = d.height < 0 ? d.y + d.height : d.y;

        const parentId = (() => {
          const containing = rectsRef.current
            .filter((r) => x >= r.x && x <= r.x + r.width && y >= r.y && y <= r.y + r.height)
            .sort((a, b) => b._depth - a._depth);
          return containing.length > 0 ? containing[0].id : null;
        })();

        const newRect = {
          x,
          y,
          width,
          height,
          name: "New Location",
          type: "",
          parent_location_id: parentId,
        };
        console.log("[debug] newRect (handleMouseUp) before save:", {
          x: newRect.x,
          y: newRect.y,
          width: newRect.width,
          height: newRect.height,
        });
        rectsRef.current.push(newRect);
        draftRef.current = null;
        updateRender();
        
        // Automatikusan edit módba kerül és kikerül a draw mode-ból
        setDrawMode(false);
        setSelectedId(null);
        saveRect(newRect, selectedCompanyId).then((result) => {
          if (result && result.success && newRect.id) {
            setSelectedId(newRect.id);
            updateRender();
            return;
          }

          // Ha mentés sikertelen, ne maradjon bent fantom elem.
          rectsRef.current = rectsRef.current.filter((r) => r !== newRect);
          updateRender();
        });
      }

      if (["dragging", "resizing"].includes(modeRef.current)) {
        modeRef.current = "idle";
        activeRectIdRef.current = null;
        draggingOrResizing = false;
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [editMode, selectedCompanyId, floorplanEditMode, floorplanEditLocation]);

  useEffect(() => {
    if (svgRef.current) updateRender();
  }, [editMode]);

  const handleTypeChange = (r, value) => {
    r.type = value;
    updateRender();
    saveRect(r, selectedCompanyId);
  };

  const handleNameChange = (r, value) => {
    r.name = value;
    updateRender();
    saveRect(r, selectedCompanyId);
  };

  const handleDelete = async (r) => {
    const children = getChildRects(r.id);
    let confirmed = true;
    if (children.length > 0) {
      confirmed = window.confirm(
        `A location-hoz (${r.name}) ${children.length} alárendelt helyszín tartozik. Ezek is törlődni fognak! Biztosan törlöd?`
      );
    }
    if (!confirmed) return;
    // Csak a deleteLocation API-t hívjuk, child esetén confirm_children=true
    await deleteLocation(r.id, children.length > 0);
    // Törlés után frissítjük a rectsRef-et és a state-et
    rectsRef.current = rectsRef.current.filter((x) => x.id !== r.id && !children.some((c) => c.id === x.id));
    setRects([...rectsRef.current]);
    setSelectedId(null);
    updateRender();
  };

  const handleEditBg = (r) => {
    // Floorplan edit mód kapcsolása az adott location-re
    const makeActive = !(floorplanEditMode && floorplanEditLocation && floorplanEditLocation.id === r.id);
    setFloorplanEditMode(makeActive);
    setFloorplanEditLocation(makeActive ? r : null);
    if (!makeActive) setFloorplanCropMode(false);
    // Keep the location selected so the panel section stays visible
    setSelectedId(r.id);
  };

  const handleAddBg = (r) => {
    if (!fileInputRef || !fileInputRef.current) return;
    if (r && r.id) setSelectedId(r.id);
    try { fileInputRef.current.value = ""; } catch (_) {}
    fileInputRef.current.click();
  };

  const toggleCropMode = () => {
    if (!floorplanEditMode || !floorplanEditLocation) return;
    setFloorplanCropMode((prev) => !prev);
    setCropSelection(null);
    pendingCropRef.current = null;
  };

  const confirmCropSelection = () => {
    if (!floorplanEditMode || !floorplanEditLocation || !cropSelection) return;
    const rectIndex = rectsRef.current.findIndex(r => r.id === floorplanEditLocation.id);
    if (rectIndex === -1) return;
    const rect = rectsRef.current[rectIndex];
    const imgX = rect.x + (rect.floorplan_offset_x || 0);
    const imgY = rect.y + (rect.floorplan_offset_y || 0);
    const localLeft = Math.max(0, cropSelection.x - imgX);
    const localTop = Math.max(0, cropSelection.y - imgY);
    const selW = Math.max(1, cropSelection.width);
    const selH = Math.max(1, cropSelection.height);
    // Beállítjuk a crop értékeket (clipPath-hoz)
    rect.floorplan_crop_left = localLeft;
    rect.floorplan_crop_top = localTop;
    rect.floorplan_crop_right = localLeft + selW;
    rect.floorplan_crop_bottom = localTop + selH;
    // Valódi crop: az eredeti kép pozícióját/méretét NEM változtatjuk,
    // csak a látható területet határozzuk meg insets-szel.
    setFloorplanEditLocation({ ...rect });
    setCropSelection(null);
    pendingCropRef.current = null;
    setFloorplanCropMode(false);
    updateRender();
  };

  const saveAndExitFloorplanEdit = async () => {
    if (floorplanEditLocation) {
      await saveRect(floorplanEditLocation, selectedCompanyId);
    }
    setFloorplanCropMode(false);
    setCropSelection(null);
    pendingCropRef.current = null;
    setFloorplanEditMode(false);
    setSelectedId(floorplanEditLocation?.id || null);
    updateRender();
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && floorplanEditMode) {
        setFloorplanEditMode(false);
        setFloorplanEditLocation(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [floorplanEditMode, setFloorplanEditMode, setFloorplanEditLocation]);

  return {
    draftRef,
    handleMouseDown,
    updateRender,
    canvasOffset,
    canvasZoom,
    handleCanvasDrag,
    handleCanvasDragEnd,
    handleCanvasZoom,
    handleTypeChange,
    handleNameChange,
    handleDelete,
    handleEditBg,
    handleAddBg,
    floorplanCropMode,
    toggleCropMode,
    cropSelection,
    confirmCropSelection,
    saveAndExitFloorplanEdit,
    animateToView,
  };
}; 